import Highcharts, {
  PointLabelObject,
  PointerAxisCoordinateObject,
  StackItemObject,
  Tooltip,
  TooltipFormatterContextObject,
} from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  NumericParameterStyle,
  Parameter,
  ParameterGroup,
} from "../../common/types/parameters";
import {
  DataTable,
  format,
  getMostDetailedStyling,
  prepareData,
  scaleToUnit,
} from "./util";

import {
  BAR_CHART_COLOR_DRIFT_UNDERHALL,
  BAR_CHART_COLOR_ELNAT_EFFEKT,
  BAR_CHART_COLOR_ELNAT_FAST,
  BAR_CHART_COLOR_ELNAT_OVERFORING,
  BAR_CHART_COLOR_ELSKATT,
  BAR_CHART_COLOR_ENERGI,
  BAR_CHART_COLOR_EXTRA_FJARRVARMESPETS,
  BAR_CHART_COLOR_EXTRA_REINVESTERING,
  BAR_CHART_COLOR_FJARRVARME_EFFEKT,
  BAR_CHART_COLOR_FLODESKOSTNAD,
  BAR_CHART_COLOR_KAPITALKOSTNAD,
} from "./barchart.colors";

export {
  BAR_CHART_COLOR_DRIFT_UNDERHALL,
  BAR_CHART_COLOR_ELNAT_EFFEKT,
  BAR_CHART_COLOR_ELNAT_FAST,
  BAR_CHART_COLOR_ELNAT_OVERFORING,
  BAR_CHART_COLOR_ELSKATT,
  BAR_CHART_COLOR_ENERGI,
  BAR_CHART_COLOR_EXTRA_FJARRVARMESPETS,
  BAR_CHART_COLOR_EXTRA_REINVESTERING,
  BAR_CHART_COLOR_FJARRVARME_EFFEKT,
  BAR_CHART_COLOR_FLODESKOSTNAD,
  BAR_CHART_COLOR_KAPITALKOSTNAD,
};

export enum Orientation {
  horizontal,
  vertical,
}

/** One non-total row in the Fjärrvärmespets tooltip (first six stack types; total is the hover line only). */
export type SupplementalHeatingTooltipRow = {
  color: string;
  label: string;
  value: any;
};

/** Tooltip breakdown when hovering “Fjärrvärmespets” – mirrors DH bar labels/colors. */
export type SupplementalHeatingTooltipBreakdown = {
  rows: SupplementalHeatingTooltipRow[];
};

/** Segment colours for Detaljer chart bars (seven stacks). Tooltip uses indices 0–5; index 6 is the hovered spets total. */
export const DETAILS_STACK_COLORS_ROW_ORDER = [
  BAR_CHART_COLOR_ELNAT_EFFEKT, // IncreasedGridCost
  BAR_CHART_COLOR_FLODESKOSTNAD, // FlowCost
  BAR_CHART_COLOR_FJARRVARME_EFFEKT, // LoadCost
  BAR_CHART_COLOR_ENERGI, // EnergyCost
  BAR_CHART_COLOR_DRIFT_UNDERHALL, // MaintenanceCost
  BAR_CHART_COLOR_KAPITALKOSTNAD, // CapitalCost
  BAR_CHART_COLOR_EXTRA_FJARRVARMESPETS, // Supplemental / Totalkostnader spets (hovered slice)
];

export const SUPPLEMENTAL_DISTRICT_HEATING_SERIES_NAME = "Fjärrvärmespets";

export default function BarChart(params: {
  parameterGroups: ParameterGroup[];
  title?: string;
  description?: string;
  unit?: string;
  orientation?: Orientation;
  xAxisLabelRotation?: number;
  displayLegends?: boolean;
  includeVat: boolean;
  toPrint: boolean;
  /** keyed by heating alternative title: mirrored DH-style rows under Fjärrvärmespets hover */
  supplementalDistrictHeatingTooltipByCategory?: Record<
    string,
    SupplementalHeatingTooltipBreakdown
  >;
}) {
  let table = prepareData(
    params.parameterGroups.filter((group) => !group.hidden)
  );
  fillWithZeroes(table.data, table.rowTitles.length, table.columnTitles.length);
  let styling = getStyling(params.parameterGroups);

  let colors =
    params.parameterGroups[0].parameters.length === 2
      ? [
          BAR_CHART_COLOR_DRIFT_UNDERHALL, // RunningCostsSum
          BAR_CHART_COLOR_KAPITALKOSTNAD, // CapitalCost
        ]
      : params.parameterGroups[0].parameters.length === 3
        ? [
            BAR_CHART_COLOR_DRIFT_UNDERHALL, // RunningCostsSum
            BAR_CHART_COLOR_EXTRA_FJARRVARMESPETS, // Supplemental district heating.
            BAR_CHART_COLOR_KAPITALKOSTNAD, // CapitalCost
          ]
        : params.parameterGroups[0].parameters.length === 7
          ? [
              BAR_CHART_COLOR_ELNAT_EFFEKT, // IncreasedGridCost
              BAR_CHART_COLOR_FLODESKOSTNAD, // FlowCost
              BAR_CHART_COLOR_FJARRVARME_EFFEKT, // LoadCost
              BAR_CHART_COLOR_ENERGI, // EnergyCost
              BAR_CHART_COLOR_DRIFT_UNDERHALL, // MaintenanceCost
              BAR_CHART_COLOR_KAPITALKOSTNAD, // CapitalCost
              BAR_CHART_COLOR_EXTRA_FJARRVARMESPETS, // SupplementalDistrictHeating
            ]
          : params.parameterGroups[0].parameters.length === 9
            ? [
                BAR_CHART_COLOR_ELNAT_EFFEKT, // LCC: Ökad elnätskostnad, effekt
                BAR_CHART_COLOR_ELNAT_FAST, // LCC: Ökad elnätskostnad, fast
                BAR_CHART_COLOR_ELNAT_OVERFORING, // LCC: Ökad elnätskostnad, rörlig
                BAR_CHART_COLOR_FLODESKOSTNAD, // FlowCost, (avkylningskostnad)
                BAR_CHART_COLOR_FJARRVARME_EFFEKT, // LoadCost
                BAR_CHART_COLOR_ENERGI, // EnergyCost
                BAR_CHART_COLOR_DRIFT_UNDERHALL, // MaintenanceCost
                BAR_CHART_COLOR_KAPITALKOSTNAD, // CapitalCost
                BAR_CHART_COLOR_EXTRA_REINVESTERING, // LCC: reinvestering
              ]
            : params.parameterGroups[0].parameters.length === 10
              ? [
                  BAR_CHART_COLOR_ELNAT_EFFEKT, // LCC: Ökad elnätskostnad, effekt
                  BAR_CHART_COLOR_ELNAT_FAST, // LCC: Ökad elnätskostnad, fast
                  BAR_CHART_COLOR_ELNAT_OVERFORING, // LCC: Ökad elnätskostnad, rörlig
                  BAR_CHART_COLOR_ELSKATT, // LCC: Elskatt
                  BAR_CHART_COLOR_FLODESKOSTNAD, // FlowCost, (avkylningskostnad)
                  BAR_CHART_COLOR_FJARRVARME_EFFEKT, // LoadCost
                  BAR_CHART_COLOR_ENERGI, // EnergyCost
                  BAR_CHART_COLOR_DRIFT_UNDERHALL, // MaintenanceCost
                  BAR_CHART_COLOR_KAPITALKOSTNAD, // CapitalCost
                  BAR_CHART_COLOR_EXTRA_REINVESTERING, // LCC: reinvestering
                ]
              : params.parameterGroups[0].parameters.length === 11
                ? [
                    BAR_CHART_COLOR_ELNAT_EFFEKT, // LCC: Ökad elnätskostnad, effekt
                    BAR_CHART_COLOR_ELNAT_FAST, // LCC: Ökad elnätskostnad, fast
                    BAR_CHART_COLOR_ELNAT_OVERFORING, // LCC: Ökad elnätskostnad, rörlig
                    BAR_CHART_COLOR_ELSKATT, // LCC: Elskatt
                    BAR_CHART_COLOR_FLODESKOSTNAD, // FlowCost, (avkylningskostnad)
                    BAR_CHART_COLOR_FJARRVARME_EFFEKT, // LoadCost
                    BAR_CHART_COLOR_ENERGI, // EnergyCost
                    BAR_CHART_COLOR_DRIFT_UNDERHALL, // MaintenanceCost
                    BAR_CHART_COLOR_KAPITALKOSTNAD, // CapitalCost
                    BAR_CHART_COLOR_EXTRA_REINVESTERING, // LCC: reinvestering
                    BAR_CHART_COLOR_EXTRA_FJARRVARMESPETS, // LCC: fjärrvärmespets
                  ]
                : DETAILS_STACK_COLORS_ROW_ORDER;

  let cleanedData = removeEmptySeries(table, colors);

  const tooltipBreakdown = params.supplementalDistrictHeatingTooltipByCategory;

  const options = {
    legend: {
      enabled: params.displayLegends,
    },
    chart: {
      height: 400,
      type: params.orientation === Orientation.horizontal ? "bar" : "column",
    },
    title: {
      text: params.title,
    },
    subtitle: {
      text: params.description,
    },
    xAxis: {
      categories: cleanedData.table.columnTitles,
      labels: {
        rotation: params.xAxisLabelRotation,
      },
    },
    colors: cleanedData.colors.reverse(),
    yAxis: {
      title: {
        text: params.unit,
      },
      stackLabels: {
        enabled: true,

        formatter: function (this: StackItemObject) {
          let heatingTechnologyIndex = this.x;
          let costTypeData = this.axis.series;
          let relevantCosts = costTypeData
            .filter((costData) => costData.visible)
            .map(
              (costData) => (costData as any).yData[heatingTechnologyIndex] ?? 0
            );
          let total = relevantCosts.reduce(
            (sum, currentCost) => sum + currentCost,
            0
          );
          return (this.total as number) > 0
            ? format(total as number, false, styling)
            : "";
        },
      },
    },
    series: cleanedData.table.data
      .map((row, index) => {
        return {
          name: cleanedData.table.rowTitles[index],
          data: row.map((parameter) =>
            scaleToUnit(
              Number(parameter.value),
              (parameter.stylingConfig as NumericParameterStyle)?.unit,
              params.includeVat
            )
          ),
        };
      })
      .reverse(),
    tooltip: {
      useHTML: true,
      formatter: function (
        this: TooltipFormatterContextObject,
        tooltip: Tooltip
      ) {
        const category = String(this.x);
        const extra =
          tooltipBreakdown &&
          this.series.name
            .toLowerCase()
            .includes(SUPPLEMENTAL_DISTRICT_HEATING_SERIES_NAME.toLowerCase())
            ? tooltipBreakdown[category]
            : undefined;

        if (extra && extra.rows?.length > 0) {
          const detailRowsHtml = extra.rows
            .map(
              (r) =>
                `<div style="display: flex; gap: 4px; align-items: baseline; margin-top: 2px;"><span style="color: ${r.color};">●</span><span>${r.label}: <strong>${format(
                  r.value,
                  params.includeVat,
                  { ...styling, unit: "kr" } as NumericParameterStyle
                )}</strong></span></div>`
            )
            .join("");
          return `
            <div style="font-size: 10px;">${category}</div>
            <div style="margin-top: 4px;">
              <span style="color: ${this.color};">●</span>
              ${this.series.name}:
              <span style="font-weight: bold;">
                ${format(
                  this.y as number,
                  false /* VAT already considered in series */,
                  styling
                )}
              </span>
            </div>
            <div style="margin-top: 8px; font-size: 11px;">
              ${detailRowsHtml}
            </div>
            `;
        }

        return `
          <div style="font-size: 10px;">${this.x}</div>
          <div>
            <span style="color: ${this.color};">●</span>
            ${this.series.name}:
            <span style="font-weight: bold;">
              ${format(
                this.y as number,
                false /* VAT already considered in series */,
                styling
              )}
            </span>
          </div>
          `;
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        animation: params.toPrint ? false : true,
      },
      bar: {
        stacking: "normal",
        animation: params.toPrint ? false : true,
      },
    },
  };

  return (
    <HighchartsReact
      key={cleanedData.table.rowTitles.join("\0")}
      containerProps={
        params.toPrint
          ? { style: { width: "130mm" } }
          : { style: { height: 400, width: "100%" } }
      }
      highcharts={Highcharts}
      options={options}
    />
  );
}

function fillWithZeroes(table: Parameter[][], rows: number, columns: number) {
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < columns; c++) {
      if (table[r] && table[r][c] === undefined) {
        table[r][c] = {
          id: "",
          value: "0",
        } as Parameter;
      }
    }
  }
}

function getStyling(parameterGroups: ParameterGroup[]): NumericParameterStyle {
  let detailedStyling = getMostDetailedStyling(
    parameterGroups.flatMap((group) => group.parameters)
  );
  return {
    numDecimals: detailedStyling.numDecimals,
    decimalSeparator: detailedStyling.decimalSeparator,
    stepSize: Math.max(100, detailedStyling.stepSize),
    thousandsSeparator: detailedStyling.thousandsSeparator,
  } as NumericParameterStyle; /* ignore unit since scaling has already been done in series */
}

function removeEmptySeries(
  table: DataTable,
  colors: string[]
): { table: DataTable; colors: string[] } {
  function isEmpty(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      value === 0 ||
      value === "0" ||
      value === 0.0 ||
      value === "0.0"
    );
  }

  let seriesToRemove = table.rowTitles
    .map((series, index) => {
      if (series === undefined || series === null) return index;
      let allEmpty =
        table.data[index]?.every(
          (param) => param === undefined || isEmpty(param.value)
        ) ?? true;
      return allEmpty ? index : -1;
    })
    .filter((index) => index !== -1);

  return {
    table: {
      columnTitles: table.columnTitles,
      rowTitles: table.rowTitles.filter(
        (title, index) => !seriesToRemove.includes(index)
      ),
      data: table.data.filter(
        (seriesData, index) => !seriesToRemove.includes(index)
      ),
    } as DataTable,
    colors: colors.filter(
      (seriesColor, index) => !seriesToRemove.includes(index)
    ),
  };
}
